import moment from "moment";
import { DATE_FORMAT, DATE_FORMAT_GUI, DATE_MONTH_FORMAT, DATE_NUMBER_FORMAT, DATE_TIME_FORMAT_GUI } from "../constants/TIME";

export const convertDateStringToNumber = date => {
    if (!date || date.length !== 10) {
        return 0;
    }
    const year = parseInt(date.substr(0, 4));
    const month = parseInt(date.substr(5, 2));
    const day = parseInt(date.substr(8, 2));

    return year * 10000 + month * 100 + day;
};

export const consolidateDateToNumber = date => {
    if (!date) return 0;
    if (typeof date === "string")
        return convertDateStringToNumber(date);
    return date;
}

export const from = interval => {
    switch (interval) {
        case "1D":
            return moment().subtract(1, "days");
        case "1W":
            return moment().subtract(1, "weeks");
        case "1M":
            return moment().subtract(1, "months");
        case "1Y":
            return moment().subtract(1, "years");
        default:
            return moment();
    }
};

export const today = () => {
    return moment();
};

// maps a number like 20200102 to a moment object
export const mapNumberToDate = number => {
    return moment(number, "YYYYMMDD");
};

export const convertDateToFullDateTime = date => {
    if (date === undefined || date?.length === 0) return "";
    return moment(date).format(DATE_TIME_FORMAT_GUI);
};

export const convertNumberToDateString = dateNumber => {
    if (!dateNumber) return "";
    const dateString = dateNumber.toString();
    return moment(dateString, "YYYYMMDD").format(DATE_FORMAT_GUI);
};

export const convertNumberToDateStringWithFormat = (dateNumber: number, format?: string) => {
    if (!dateNumber) return "";
    const dateString = dateNumber.toString();
    const usedFormat = format || DATE_FORMAT_GUI;
    
    return moment(dateString, "YYYYMMDD").format(usedFormat);
};

export function dateFormat(dat) {
    if (dat.length !== 8) {
        return dat;
    }
    const year = parseInt(dat.substr(0, 4));
    const month = parseInt(dat.substr(4, 2));
    const day = parseInt(dat.substr(6, 2));
    return day + "." + month + "." + year;
}

export function fromToString(from, to) {
    return convertNumberToDateString(from) + " - " + convertNumberToDateString(to);
}

export const isFromToValid = (from, to) => {
    const momentDateFrom = moment(from, DATE_FORMAT);
    const momentDateTo = moment(to, DATE_FORMAT);

    const minYear = 1000;
    return (momentDateFrom.year() > minYear && momentDateTo.year() > minYear && !momentDateFrom.isAfter(momentDateTo));
};

export const createDefaultStartDate = () => {
    return moment().subtract(1, "days").subtract(1, "months").format(DATE_FORMAT);
}

export const creatDefaultEndDate = () => {
    return moment().subtract(1, "days").format(DATE_FORMAT);
}

export const getStartDate = (lastAvailableDate) => {
    if (!lastAvailableDate) return createDefaultStartDate();
    return mapNumberToDate(lastAvailableDate).subtract(1, "months").format(DATE_FORMAT);
};

export const numberToDateFormat = (intDate) => {
    return mapNumberToDate(intDate).format(DATE_FORMAT);
};

export const getEndDate = (lastAvailableDate) => {
    if (!lastAvailableDate) return creatDefaultEndDate();
    return numberToDateFormat(lastAvailableDate);
};

export const isDateInvalid = (from: string, to: string): boolean => {
    const momentDateFrom = moment(from, DATE_FORMAT);
    const momentDateTo = moment(to, DATE_FORMAT);

    return momentDateFrom.isAfter(momentDateTo);
}

export const isDateOutsideRange = (from: string, to: string, firstAvailableDate: string, lastAvailableDate: string): boolean => {
    const momentDateFrom = moment(from, DATE_FORMAT);
    const momentDateTo = moment(to, DATE_FORMAT);
    const momentFirstAvailableDate = moment(firstAvailableDate, DATE_FORMAT);
    const momentLastAvailableDate = moment(lastAvailableDate, DATE_FORMAT);

    return momentDateFrom.isBefore(momentFirstAvailableDate) || momentDateTo.isAfter(momentLastAvailableDate);
}

export const isDateSameOrAfterDate = (date: number, limit: number): boolean => {
    if (limit === undefined || limit === null  || limit === 0) return true;
    const momentDate = moment(date, DATE_NUMBER_FORMAT);
    const momentLimit = moment(limit, DATE_NUMBER_FORMAT);

    return momentDate.isSameOrAfter(momentLimit);
}

export const isDateSameOrBeforeDate = (date: number, limit: number): boolean => {
    if (limit === undefined || limit === null || limit === 0) return true;
    const momentDate = moment(date, DATE_NUMBER_FORMAT);
    const momentLimit = moment(limit, DATE_NUMBER_FORMAT);

    return momentDate.isSameOrBefore(momentLimit);
}

export const isDateInsideRange = (date: number, from: number, to: number): boolean => {
    return isDateSameOrAfterDate(date, from) && isDateSameOrBeforeDate(date, to);
}

export const hasMinimalDaysError = (minimalDays: number, noFiles): boolean => {
    return minimalDays !== 0 && noFiles < minimalDays;
}

export const isDateInPast = (date: string): boolean => {
    const momentDate = moment(date, DATE_FORMAT);
    return (today().isAfter(momentDate, 'day'));
}

export const UNRESTRICTED = "Unrestricted";

export function toDate(endDate) {
    return endDate ? convertNumberToDateString(endDate) : UNRESTRICTED;
}

export function toDateRange(dateRange) {
    return dateRange?.startDate || dateRange?.endDate
        ? toDate(dateRange?.startDate) + " - " + toDate(dateRange?.endDate)
        : UNRESTRICTED;
}

export const isDateInPastMonths = (date: string): boolean => {
    const momentDate = moment(date, DATE_FORMAT);
    return (today().isAfter(momentDate, 'month'));
}

export const isDateMonthValid = (dateMonth: string): boolean => {
    const momentDate = moment(dateMonth, DATE_MONTH_FORMAT);
    return momentDate.isValid();
}

export const getDefaultVisibilityEndDate = (endDate: number, isFilterableSubscription: boolean): number => {
    const momentEndDate = moment(endDate, DATE_FORMAT);
    const momentDate = isFilterableSubscription ? momentEndDate.add(7, "d") : momentEndDate.add(2, "years");
    return convertDateStringToNumber(momentDate.format(DATE_FORMAT));
}